import React, { useState, useEffect } from 'react';
import ModelPromptView from './ModelPrompts/View';
import ModelPromptEdit from './ModelPrompts/Edit';
import DeleteConfirm from './ModelPrompts/DeleteConfirm';
import { useAgencyInfo } from 'context/agency';
import { useUserInfo } from 'context/user';
import { Switch, IconButton, Icon } from '@mui/material';
import "./style.css";
import { useAvailableBranding, useUpdatePartnerTask, useDeletePartnerTask } from 'queries/prompts';
import { useManagePromptDetails } from 'queries/promptCategories';

function Prompts({ initialSubcategory_prompts_Data, refresh, roleFilter, sectorFilter, searchQuery }) {
  const [loader, setLoader] = useState(false);
  const [isModalOpenView, setIsModalOpenView] = useState(false);
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [modalData, setModalData] = useState({});
  const [modalDataEdit, setModalDataEdit] = useState({});
  const [agency] = useAgencyInfo();
  const user = useUserInfo()[0];

  const [subcategory_prompts_data, setSubcategory_prompts_Data] = useState(initialSubcategory_prompts_Data);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    setSubcategory_prompts_Data(initialSubcategory_prompts_Data);
  }, [initialSubcategory_prompts_Data]);

  const { data: integrationsList = [] } = useAvailableBranding({
    enabled: !!(agency?._id && user?.user_id),
  });

  const { mutateAsync: updatePromptActive } = useUpdatePartnerTask();

  const { mutateAsync: deletePromptMutation } = useDeletePartnerTask();

  const { mutateAsync: getManagePromptDetails } = useManagePromptDetails();

  const handleSwitchChange = async (id) => {
    const prompt = subcategory_prompts_data.find((p) => p._id === id);
    if (!prompt) return;

    const newStatus = !prompt.active;
    const payload = {
      prompt_id: id,
      agency_id: agency._id,
      active: newStatus,
    };

    const { response } = await updatePromptActive(payload);
    if (response) {
      setSubcategory_prompts_Data((prev) =>
        prev.map((p) => (p._id === id ? { ...p, active: newStatus } : p))
      );
    }
  };

  const handleDelete_sub_Category_prompt = (id, idx) => {
    setDeleteTargetId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;

    const { response } = await deletePromptMutation(deleteTargetId);

    if (response) {
      refresh();
      setDeleteTargetId(null);
    }
  };

  const openModelView = async (data) => {
    const { response } = await getManagePromptDetails({ agencyId: agency._id, promptId: data._id });
    if (response) {
      setModalData(response.data);
      setIsModalOpenView(true);
    }
  };

  const openModelEdit = async (data) => {
    const { response } = await getManagePromptDetails({ agencyId: agency._id, promptId: data._id });
    if (response) {
      setModalDataEdit({ ...response.data, category: data.category || response.data.category });
      setIsModalOpenEdit(true);
    }
  };

  const Editdata = async (formData) => {
    setLoader(true);
    const updatedFormData = { ...formData, agency_id: agency._id };

    const { response } = await updatePromptActive(updatedFormData);
    if (response) {
      handleclose();
      setLoader(false);
      refresh();
    }
  }

  const handleclose = () => {
    setIsModalOpenView(false)
    setIsModalOpenEdit(false)
  }

  const filteredPrompts = subcategory_prompts_data?.filter((prompt) => {
    const roleMatch = roleFilter === 'all' || (prompt.job_roles && prompt.job_roles.includes(roleFilter));
    const sectorMatch = sectorFilter === 'all' || (prompt.sectors && prompt.sectors.includes(sectorFilter));
    const searchMatch = !searchQuery || (prompt.title && prompt.title.toLowerCase().includes(searchQuery.toLowerCase()));
    return roleMatch && sectorMatch && searchMatch;
  }) || [];

  const getLabel = (id, list) => {
    const item = list.find(l => l.value === id || l._id === id || l.id === id);
    return item ? (item.label || item.name) : id;
  };

  // A task's category is a single string, but existing data can hold a comma-separated
  // list ("Marketing, Reports, Content") and the value may be the agency category id or
  // its name. Resolve the first entry only, and return null when it can't be resolved so
  // we never render a raw id as a tag.
  const getCategoryLabel = (category) => {
    const first = String(category || '').split(',')[0].trim();
    if (!first) return null;
    const item = (agency.Categories || []).find(
      c => c.id === first || c._id === first || (c.name || '').toLowerCase() === first.toLowerCase()
    );
    return item ? item.name : null;
  };

  const Tag = ({ text, color = '#F4F7F9' }) => (
    <span style={{
      background: color,
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      color: '#495057',
      marginRight: '8px',
      marginTop: '8px',
      display: 'inline-block'
    }}>
      {text}
    </span>
  );

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {filteredPrompts.map((data, idx) => (
          <div key={data._id} style={{
            background: '#FFFFFF',
            border: '1px solid #E9ECEF',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A1A', margin: '0 0 10px 0' }}>{data.title}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {getCategoryLabel(data.category) && (
                  <Tag text={getCategoryLabel(data.category)} />
                )}
                {data.job_roles?.map(roleId => (
                  <Tag key={roleId} text={getLabel(roleId, agency.job_roles || [])} />
                ))}
                {data.sectors?.map(sectorId => (
                  <Tag key={sectorId} text={getLabel(sectorId, agency.sectors || [])} />
                ))}
                {data.integrations?.map(intId => (
                  <Tag key={intId} text={getLabel(intId, integrationsList)} color="#F1F3F5" />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IconButton onClick={() => openModelEdit(data)} size="small">
                <Icon sx={{ color: '#ADB5BD', fontSize: '20px !important' }}>edit</Icon>
              </IconButton>
              <IconButton onClick={() => handleDelete_sub_Category_prompt(data._id, idx)} size="small">
                <Icon sx={{ color: '#ADB5BD', fontSize: '20px !important' }}>delete</Icon>
              </IconButton>
              <Switch
                checked={data.active}
                onChange={() => handleSwitchChange(data._id)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#000' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#000' }
                }}
              />
            </div>
          </div>
        ))}
        {filteredPrompts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6C757D' }}>
            No tasks found for the selected criteria.
          </div>
        )}
      </div>

      {isModalOpenView && (
        <ModelPromptView data={modalData} modelfunctionality={handleclose} openModel={isModalOpenView} onclosemodel={handleclose} />
      )}
      {isModalOpenEdit && (
        <ModelPromptEdit data={modalDataEdit} modelfunctionality={Editdata} openModel={isModalOpenEdit} onclosemodel={handleclose} />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirm
          openModel={isDeleteModalOpen}
          onclosemodel={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

export default Prompts;
